import { useCallback, useRef, useState } from 'react'
import { HookLike, ListHookFlags } from 'redux_toolkit/lib/createPagedListApi'
import { C1PagedList, C1PagedListProps } from './C1PagedList'

type C1PagedListHookProps<ReqT, T, ExtraProps> = {
    hook: HookLike<ReqT, T>
    request: ReqT
    hookFlags?: ListHookFlags
} & Pick<C1PagedListProps<T, ExtraProps>, 'emptyState' | 'getId' | 'totalRowCount' | 'ListItemComponent' | 'ListItemSkeleton' | 'sx' | 'staticExtraProps' | 'usePageNumbers' | 'hidePageLabel' | 'pageSize'>

export const C1PagedListHook = <ReqT, T, ExtraProps = object>({
    emptyState,
    getId = (x): string => (x as { id: string }).id,
    totalRowCount,
    ListItemComponent,
    ListItemSkeleton,
    hook: useHook,
    request,
    hookFlags,
    sx,
    staticExtraProps: _staticExtraProps = {} as ExtraProps,
    usePageNumbers = false,
    hidePageLabel = false,
    pageSize: _pageSize = 50,
}: C1PagedListHookProps<ReqT, T, ExtraProps>): React.ReactNode => {
    const [page, setPage] = useState<number>(0)
    const { pageSize, notify, ...rest } = { pageSize: _pageSize, ...hookFlags }

    const extraProps = useRef(_staticExtraProps).current
    const { data, isError, isLoading, isBackgroundLoading, error, canLoadMore, getPage } = useHook(request, {
        pageSize,
        notify,
        ...rest,
    })

    const onPageChange = useCallback(
        async (n: number) => {
            await getPage(n)
            setPage(n)
        },
        [getPage],
    )

    return (
        <C1PagedList<T, ExtraProps>
            data={data}
            isError={isError}
            isLoading={isLoading}
            error={error}
            canLoadMore={canLoadMore}
            pageSize={pageSize}
            page={page}
            onPageChange={onPageChange}
            emptyState={emptyState}
            getId={getId}
            ListItemComponent={ListItemComponent}
            ListItemSkeleton={ListItemSkeleton}
            staticExtraProps={extraProps}
            sx={sx}
            usePageNumbers={usePageNumbers}
            hidePageLabel={hidePageLabel}
            totalRowCount={totalRowCount}
            isBackgroundLoading={isBackgroundLoading}
        />
    )
}
